import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'

import { API_URL } from '../../api'
import BGLeft from '../../assets/images/BGLeft.svg'

// The app shell hides the desktop sidebar / switches to mobile chrome at 800px;
// the chat feature follows the same breakpoint for a coherent transition.
export const CHAT_MOBILE_VIEWPORT = '(max-width: 800px)'
export const CHAT_MOBILE = `@media ${CHAT_MOBILE_VIEWPORT}`

// Mobile horizontal gutters: the transcript's content gutter (CenterColumn) and
// the footer's, which is what sets the input field's left edge (FooterInner).
// The empty-state pill row breaks out of the first and re-insets by the second
// so its first pill lines up with the input — keep all three in sync.
const MOBILE_CONTENT_GUTTER = 16
const MOBILE_FOOTER_GUTTER = 12

// The SBSA chat panel / overlay reuses the transcript primitives inside its own
// card, which is inset by 20px on every side (design). Exported so the panel's
// header, footer and pill rows share the one gutter.
export const PANEL_GUTTER = 20

// Corner radius of the input pill (InputShell) in each sizing.
const INPUT_RADIUS = 28
const INPUT_RADIUS_COMPACT = 21
export const inputRadius = compact => (compact ? INPUT_RADIUS_COMPACT : INPUT_RADIUS)

/*
 * How far the transcript reaches down over the footer, so that content ends up
 * behind the input pill instead of being clipped short of it (see Transcript).
 *
 * It has to land in the band BELOW the pill's corner arcs and ABOVE its bottom
 * edge, because that is the only depth where the pill spans the full content
 * column and hides the transcript's clip line completely. The arcs end one radius
 * down, so one radius is not enough: at exactly that depth the line is tangent to
 * each arc at a single point and still cuts straight across the rest of the two
 * corner notches — the "invisible rectangle beside the input" this is meant to
 * get rid of. A little past the arcs the notches are gone.
 *
 * The upper bound is the pill's own collapsed height (43px compact, 61px full),
 * and typing only ever makes it taller, so there is plenty of room under this.
 */
const ARC_CLEARANCE = 8
export const inputOverhang = compact => inputRadius(compact) + ARC_CLEARANCE

// Desktop cap for the transcript and footer columns. It is deliberately lifted
// under CHAT_MOBILE: mobile chrome is already full-width (sidebar hidden, mobile
// gutters), and leaving the cap on made both columns centered with auto margins
// between 761px and 800px. That pushed the input's left edge to
// `autoMargin + MOBILE_FOOTER_GUTTER` instead of a flat gutter, so the pill row
// below could not line up with it and its full-bleed break-out could not reach
// the screen edges without overflowing Transcript's overflow-x: hidden.
const CONTENT_MAX_WIDTH = 760

const primary = p => p.theme.primaryColor ?? '#038ab7'
const primaryLighter = p => p.theme.primaryLighter ?? '#d4ebf2'
const surface = p => p.theme.topNavColor ?? '#ffffff'
const bodyText = p => p.theme.bodyTextColor ?? '#4a606a'
const mutedText = p => p.theme.lightestGray ?? '#7a8e96'

/* ── Layout ─────────────────────────────────────────────────────────── */

// Full-screen focused shell for the standalone /hey-nic route: the focused top
// nav, then a row of the chat-history sidebar + the centered chat content.
export const FocusedShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh; /* fallback */
  /* Dynamic viewport so the shell tracks the collapsing URL bar. dvh does NOT
     account for the on-screen keyboard, so HeyNicChat overrides this height with
     the measured visual-viewport height while the keyboard is open (see
     useKeyboardInset) — that's what keeps the footer pinned above it. */
  height: 100dvh;
  /* The keyboard-open height is an exact fit; never let content stretch it. */
  overflow: hidden;
`

export const FocusedBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`

export const FocusedContent = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  /* Part of the unbroken min-height: 0 chain from the shell down to Transcript.
     Any link missing it lets a flex item refuse to shrink below its content, and
     the transcript stops shrinking when the keyboard shortens the shell — content
     then scrolls around underneath the input instead. */
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* theme.bgLeft is an image path served from the API; fall back to the bundled
     asset when it's unset. */
  background: url(${p => (API_URL && p.theme.bgLeft ? API_URL + p.theme.bgLeft : BGLeft)}) no-repeat
    top left;
  background-color: ${p => p.theme.homeBgColor};
  background-size: cover;

  ${CHAT_MOBILE} {
    background-image: none;
  }
`

// Fills the routed content area; the transcript scrolls, the footer overlays.
export const HeyNicWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

// Positioned wrapper that ends at the footer's top edge; the transcript scrolls
// within it and the floating scroll-to-bottom button anchors to its bottom.
export const ScrollArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

export const Transcript = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /*
   * Reach down over the in-flow footer, far enough that the clip edge falls
   * inside the input pill rather than anywhere it can be seen. Without it the
   * edge is a full-width straight line resting on the pill's top edge, and
   * content vanishes at an invisible rectangle — including in the two gaps beside
   * the pill's rounded corners, where nothing is covering it at all.
   *
   * The pill is what hides the edge: it is opaque, at least as wide as the
   * content column in every layout, and positioned + later in the DOM, so it
   * paints over the transcript. See inputOverhang for how far down is far enough.
   */
  margin-bottom: -${p => inputOverhang(p.compact)}px;
  /* Breathing room above the input at rest, on top of the overlap it has to
     out-reach — the last message should park above the pill, not under it. */
  padding: 8px 0 ${p => 16 + inputOverhang(p.compact)}px;
`

// Horizontally centered within whatever space remains (sidebar collapsed or not).
export const CenterColumn = styled.div`
  width: 100%;
  max-width: ${p => (p.compact ? 'none' : `${CONTENT_MAX_WIDTH}px`)};
  margin: 0 auto;
  /* The SBSA panel's own 20px content gutter (see PANEL_GUTTER). */
  padding: 0 ${p => (p.compact ? PANEL_GUTTER : 24)}px;
  box-sizing: border-box;
  ${CHAT_MOBILE} {
    max-width: none;
    padding: 0 ${p => (p.compact ? PANEL_GUTTER : MOBILE_CONTENT_GUTTER)}px;
  }
`

/* ── Empty state (brand-new chat) ───────────────────────────────────── */

// The empty state's icon-only Hey Nic mark. Hidden on mobile: the top nav already
// carries the HEY NIC wordmark right above it, and the 45px mark plus its gap is
// the difference between the starter pills fitting above the keyboard and being
// clipped off the bottom of a shortened transcript.
export const EmptyStateMark = styled.div`
  line-height: 0;
  ${CHAT_MOBILE} {
    display: none;
  }
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  padding: 8vh 0 24px;
  ${CHAT_MOBILE} {
    /* With the keyboard up the transcript can be only a couple hundred px tall,
       and a viewport-relative top pad (8vh of the FULL viewport) would push the
       greeting and starter pills straight out of it. No bottom pad either: there
       is no space going spare to fill with it, and any that doesn't fit costs the
       pills their place on screen. */
    gap: 16px;
    padding: 16px 0 0;
  }
`

export const OpeningQuestion = styled.h2`
  margin: 0 0 20px 0;
  font-family: 'Aleo', 'Open Sans', 'Helvetica Neue', serif;
  font-size: 26px;
  font-weight: 700;
  color: ${bodyText};
  ${CHAT_MOBILE} {
    font-size: 22px;
    margin-bottom: 8px;
  }
`

/* ── Date separators ────────────────────────────────────────────────── */

export const DateSeparator = styled.div`
  text-align: center;
  margin: 20px 0 12px;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${mutedText};
`

/* ── Message rows / bubbles ─────────────────────────────────────────── */

/*
 * The narrow-column bubble layout: icon + timestamp together on one row with the
 * bubble spanning the full width below. It applies on a mobile viewport, and —
 * via the `compact` prop — inside any narrow container on a desktop one, which
 * is what the SBSA side panel needs ("the side panel mostly borrows from the
 * mobile version due to lack of space"). A media query can't express that: the
 * viewport is wide, the container isn't.
 */
const botOuterNarrow = `
  /* Mobile: icon + timestamp sit together above the bubble with a 10px gap. */
  row-gap: 10px;
  & > .bot-avatar {
    grid-row: 1;
    align-self: center;
  }
  & > .bot-bubble,
  & > .bot-suggest {
    grid-column: 1 / -1;
  }
`

// Two-column grid: avatar | (timestamp / bubble). On desktop the avatar sits in
// the bubble's row (its top aligns with the bubble, not the timestamp). On mobile
// — or in a `compact` container — it moves up beside the timestamp (10px gap) and
// the bubble spans full width below.
export const BotOuter = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 10px;
  row-gap: 7px;
  align-items: start;
  margin: 14px 0;

  & > .bot-avatar {
    grid-column: 1;
    grid-row: 2;
  }
  & > .bot-time {
    grid-column: 2;
    grid-row: 1;
    /* Row-gap handles the space above the bubble; center against the avatar. */
    margin-bottom: 0;
    align-self: center;
  }
  & > .bot-bubble {
    grid-column: 2;
    grid-row: 2;
  }
  & > .bot-suggest {
    grid-column: 2;
    grid-row: 3;
  }

  ${p => (p.compact ? botOuterNarrow : '')}

  ${CHAT_MOBILE} {
    ${botOuterNarrow}
  }
`

export const HumanRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 14px 0;
`

export const AvatarWrap = styled.div`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  line-height: 0;
  & svg {
    width: 36px;
    height: 36px;
  }
`

// The Hey Nic response icon: fixed 30×30, top-aligned with the bubble.
export const BotAvatar = styled.div`
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  line-height: 0;
  & svg {
    display: block;
    width: 30px;
    height: 30px;
  }
`

export const MessageColumn = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 88%;
  min-width: 0;
`

export const Timestamp = styled.div`
  font-size: 12px;
  color: ${mutedText};
  margin: 0 4px 7px;
  text-align: ${p => (p.align === 'right' ? 'right' : 'left')};
`

const borderSpin = keyframes`
  to { transform: translate(-50%, -50%) rotate(360deg); }
`

/*
 * Never let a bubble outgrow its column.
 *
 * The bubbles are shrink-to-fit (align-self: flex-start), and fit-content is
 * `min(max-content, max(min-content, available))` — so anything that makes a
 * descendant's MIN-content width large (a nowrap run, a long unbroken token, a
 * flex/grid child that refuses to shrink) sizes the bubble past the column and
 * the transcript's `overflow-x: hidden` clips it. Capping at 100% forbids that
 * outright instead of depending on every descendant's intrinsic width behaving,
 * which is what a long streaming status update kept getting wrong.
 */
const bubbleFitsColumn = `
  max-width: 100%;
  min-width: 0;
`

// Wraps the bot bubble while a response streams in, drawing an animated blue
// sequence circling the border (mirrors the idle chat-input border treatment).
export const StreamingBubble = styled.div`
  position: relative;
  align-self: flex-start;
  ${bubbleFitsColumn}
  border-radius: 6px 18px 18px 18px;
  padding: 2px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 250%;
    height: 250%;
    transform: translate(-50%, -50%);
    background: conic-gradient(
      from 0deg,
      transparent 0deg,
      ${primaryLighter} 40deg,
      ${primary} 90deg,
      ${primaryLighter} 140deg,
      transparent 180deg,
      transparent 360deg
    );
    animation: ${borderSpin} 1.6s linear infinite;
  }
`

// One corner is intentionally square (nearest the avatar / sender side).
export const BotBubble = styled.div`
  position: relative;
  align-self: flex-start;
  ${bubbleFitsColumn}
  background: ${surface};
  border: ${p => (p.loading ? 'none' : `1px solid ${p.theme.ternaryColor ?? '#e7f3f9'}`)};
  color: ${bodyText};
  /* While loading, a slightly smaller top pad lines the first status row up with
     the pinned expand chevron (top: 15px). */
  padding: ${p => (p.loading ? '14px 20px 20px' : '20px')};
  border-radius: 4px 16px 16px 16px;
  font-size: 15px;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 8px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 20px;
  }
`

export const HumanBubble = styled.div`
  align-self: flex-end;
  ${bubbleFitsColumn}
  /* primaryColor at ~5% opacity (0x0d ≈ 0.05), no border. */
  background: ${p => p.theme.primaryColor ?? '#038ab7'}0d;
  color: ${bodyText};
  padding: 20px;
  border-radius: 16px 4px 16px 16px;
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
`

/* ── Loading indicators ─────────────────────────────────────────────── */

const barSlide = keyframes`
  0% { left: -40%; width: 40%; }
  50% { left: 30%; width: 55%; }
  100% { left: 100%; width: 40%; }
`

// Indeterminate GC-primary bar, shown before any text streams in.
export const BarLoader = styled.div`
  position: relative;
  height: 4px;
  width: 160px;
  max-width: 100%;
  border-radius: 2px;
  background: ${primaryLighter};
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    height: 100%;
    border-radius: 2px;
    background: ${primary};
    animation: ${barSlide} 1.3s ease-in-out infinite;
  }
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

export const Spinner = styled.span`
  /*
   * Hold its size in a flex row. It is a flex item in both StatusRow and
   * ChecklistIcon, and an empty span's min-content width is 0 — so with the
   * default flex-shrink: 1, a wrapping label (flex: 1 1 auto, large base size)
   * makes the row distribute negative free space across both items and collapse
   * the spinner's content box toward 0. Its height doesn't shrink with it, so the
   * circle renders as a vertical ellipse.
   */
  flex: 0 0 auto;
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid ${primaryLighter};
  border-top-color: ${primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

/* ── Streaming status (current step + expandable checklist) ─────────── */

/*
 * Gutter every row in the chevron's band has to keep clear.
 *
 * StatusChevron is taken out of flow and pinned to the bubble's top-right
 * corner, so nothing reflows around it: it ends 10px from the bubble's border
 * edge and is 30px wide, which reaches 20px past the content box (BotBubble pads
 * 20px horizontally). 28px leaves that plus a little slack, and both the
 * collapsed row and the checklist's first row must reserve it — otherwise short
 * status text runs underneath the caret.
 */
const CHEVRON_GUTTER = 28

// Current step: in-progress indicator + label + expand chevron. The whole row
// is one touch target that expands the full step checklist below it.
export const StatusRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 20px;
  /* Clear the pinned chevron in the top-right corner. */
  padding: 0 ${CHEVRON_GUTTER}px 0 0;
  border: none;
  background: none;
  cursor: ${p => (p.expandable ? 'pointer' : 'default')};
  color: ${bodyText};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  /*
   * Only the label flexes — not the spinner (also a span).
   *
   * The label WRAPS rather than truncating. It used to be white-space: nowrap
   * plus an ellipsis, which never got the chance to fire: the bubble is
   * shrink-to-fit (align-self: flex-start), and nowrap makes the label's
   * min-content width its full text width, so a long status pushed the bubble
   * past the transcript instead of the label ellipsing inside it. Wrapping puts
   * min-content back to the longest word, so the bubble can never be forced
   * wider than the column, and matches how BotBubble already handles response
   * text. overflow-wrap: anywhere covers a single unbroken token longer than the
   * column.
   */
  & > .hn-status-label {
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`

// The 3n expand chevron: pinned to the bubble's top-right corner (15px/10px);
// points down when collapsed, rotates up when expanded.
export const StatusChevron = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  & svg {
    display: block;
    width: 30px;
    height: 30px;
    transform: rotate(${p => (p.open ? '180deg' : '0deg')});
    transition: transform 0.2s;
  }
`

// Expanded checklist of every step run so far.
export const Checklist = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  /* The chevron stays pinned to the bubble's corner while the list is open, and
     the first row is the only one inside its band — reserve the same gutter the
     collapsed row does, so the caret can never sit on the label however short
     the status text is. Applied to the row rather than its label so a wrapped
     second line keeps the same right edge. */
  & > *:first-child {
    padding-right: ${CHEVRON_GUTTER}px;
  }
`

export const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: ${bodyText};

  /* The label, i.e. the span after the fixed-width icon slot — never the slot
     itself. Same wrapping guarantee as StatusRow: these are the same status
     strings, so they must not be able to widen the shrink-to-fit bubble either. */
  & > span + span {
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`

// Fixed 20px slot so the check badge and in-progress spinner share a column.
export const ChecklistIcon = styled.span`
  flex: 0 0 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

// Filled brand-blue check badge (color drives the circle via currentColor).
export const CheckBadge = styled.span`
  display: inline-flex;
  color: ${primary};
  & svg {
    display: block;
  }
`

/* ── Suggestion pills ───────────────────────────────────────────────── */

// The empty-state starters collapse to a single horizontally scrolling row on
// mobile, where the wrapped grid ate too much of the (keyboard-shortened)
// viewport. `-webkit-overflow-scrolling: touch` gives iOS momentum scrolling and
// the scrollbar is hidden so the row reads as a clean strip.
const scrollingRow = `
  ${CHAT_MOBILE} {
    /* Stretch to the content column: EmptyState centers its children, so an
       unstretched row would hug its pills and never overflow. */
    align-self: stretch;
    box-sizing: border-box;
    flex-wrap: nowrap;
    justify-content: flex-start;
    column-gap: 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    /* Never shrink or wrap a pill — long ones scroll off instead. */
    & > button {
      flex: 0 0 auto;
      white-space: nowrap;
    }

    /* Full-bleed: the negative margins cancel CenterColumn's content gutter so
       the scroll viewport spans the whole screen, and the horizontal padding
       re-insets the first pill to the input field's left edge (FooterInner's
       gutter) while leaving a trailing gap after the last one. Both columns are
       full-width here (see CONTENT_MAX_WIDTH), so this lands exactly on the
       screen edge and exactly on the input's edge at every width in the range.
       Carrying the content gutter through instead would stop the row short of
       the screen edges, reading as clipped rather than scrolling off-screen —
       the bug this mirrors from SBSA's account tabs. */
    margin: 0 -${MOBILE_CONTENT_GUTTER}px;
    /* 2px vertical keeps the pill borders off the scroll box's edges. */
    padding: 2px ${MOBILE_FOOTER_GUTTER}px;
  }
`

export const PillRow = styled.div`
  display: flex;
  gap: 12px;
  /* In-chat follow-ups stack one per row, right-aligned over the Send button;
     the empty-state starters wrap in a centered grid. */
  ${p =>
    p.stacked
      ? `
        flex-direction: column;
        align-items: flex-end;
      `
      : `
        flex-wrap: wrap;
        justify-content: center;
        ${scrollingRow}
      `}
`

export const Pill = styled.button`
  cursor: pointer;
  padding: 10px 18px;
  border-radius: 22px;
  border: 1px solid ${primaryLighter};
  background: ${surface};
  color: ${primary};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  font-weight: 600;
  transition: 0.2s;
  &:hover:not(:disabled) {
    background: ${primaryLighter};
    border-color: ${primary};
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

/* ── Footer / input ─────────────────────────────────────────────────── */

// In-flow bottom bar: the transcript above it owns the scroll area, rather than
// scrolling behind an overlay. It does reach a little way down over this, far
// enough to end up behind the input pill (see inputOverhang).
export const Footer = styled.div`
  flex: 0 0 auto;
  /* No top padding: it read as a buffer of dead space that content disappeared
     into before it ever reached the input. The gap above the pill at rest comes
     from the transcript's padding-bottom instead, which scrolls away. */
  padding: ${p => (p.compact ? '0' : '0 0 20px')};
  /* No background fill: the FocusedContent gradient shows through behind the
     input and disclaimer so it reaches the bottom of the screen. */
  ${CHAT_MOBILE} {
    /*
     * Always reserve the system inset, keyboard or not. Android Chrome draws
     * edge-to-edge, so visualViewport.height includes the strip behind the gesture
     * nav bar while the keyboard sits above it — the measured height overshoots the
     * usable area by roughly this inset, and the pinned shell inherits the error.
     * Reserving it here absorbs that overshoot and is what keeps the AI-disclaimer
     * caption on screen, which is a legal requirement.
     *
     * An earlier pass zeroed this while the keyboard was open, chasing a reported
     * dead gap under the input on Android. That gap belonged to the in-flow shell,
     * where the browser's reveal-scroll dragged the bottom edge off the keyboard;
     * pinning fixed that cause, and zeroing the pad then clipped the caption
     * instead. Fail-safe direction: reserving space can only push the caption up
     * into view, never out of it.
     */
    padding-bottom: max(16px, env(safe-area-inset-bottom));
  }
`

export const FooterInner = styled.div`
  width: 100%;
  max-width: ${p => (p.compact ? 'none' : `${CONTENT_MAX_WIDTH}px`)};
  margin: 0 auto;
  padding: ${p => (p.compact ? '0' : '0 24px')};
  box-sizing: border-box;
  pointer-events: auto;
  ${CHAT_MOBILE} {
    /* Matches CenterColumn so the pill row's break-out can align to the input. */
    max-width: none;
    padding: ${p => (p.compact ? '0' : `0 ${MOBILE_FOOTER_GUTTER}px`)};
  }
`

// Wraps the input to draw a static border around it.
export const InputShell = styled.div`
  position: relative;
  border-radius: ${p => inputRadius(p.compact)}px;
  padding: 2px;
  background: ${primaryLighter};
  overflow: hidden;
`

export const InputInner = styled.div`
  position: relative;
  display: flex;
  align-items: ${p => (p.compact ? 'center' : 'flex-end')};
  gap: ${p => (p.compact ? '6px' : '10px')};
  border-radius: ${p => (p.compact ? '19px' : '26px')};
  background: ${surface};
  padding: ${p => (p.compact ? '0 6px 0 18px' : '8px 8px 8px 18px')};
`

export const TextArea = styled.textarea`
  flex: 1 1 auto;
  min-width: 0;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  line-height: 1.45;
  color: ${bodyText};
  padding: ${p => (p.compact ? '8px 0' : '9px 0')};
  /* ~6 lines, then scroll internally (3 in the narrow SBSA panel). Height itself
     is set imperatively. */
  max-height: ${p => (p.compact ? '66px' : '132px')};
  overflow-y: auto;

  &::placeholder {
    font-size: 16px;
    font-weight: 300;
    color: ${p => p.theme.lighterGray ?? '#c8d2d6'};
  }
  &:disabled {
    background: transparent;
  }
`

export const SendButton = styled.button`
  flex: 0 0 auto;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: ${p => (p.compact ? '30px' : '84px')};
  width: ${p => (p.compact ? '30px' : 'auto')};
  height: ${p => (p.compact ? '30px' : '44px')};
  padding: ${p => (p.compact ? '0' : '0 22px')};
  border: none;
  border-radius: ${p => (p.compact ? '50%' : '22px')};
  color: #ffffff;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 600;
  background: ${p => p.theme.primaryButtonColor ?? '#038ab7'};
  transition: 0.2s;

  /* The arrow is a stroked icon; keep it white against the button in both layouts. */
  & svg .icon-color {
    stroke: #ffffff;
  }

  &:disabled {
    cursor: default;
    background: ${p => p.theme.primaryButtonDisabledColor ?? '#d4ebf2'};
  }
  &:hover:not(:disabled) {
    background: ${p => p.theme.primaryButtonHoverColor ?? '#02769d'};
  }
  /* The compact variant is already an icon-only circle; keep its own size. */
  ${CHAT_MOBILE} {
    min-width: ${p => (p.compact ? '30px' : '44px')};
    padding: 0;
    width: ${p => (p.compact ? '30px' : '44px')};
    border-radius: 50%;
  }
`

/* ── In-page AI comment card ────────────────────────────────────────── */

/*
 * The chatbot's opening comment shown inline on a page, which opens the full
 * chat when tapped. Both narrow-viewport entry points use it — SBSA's, above the
 * step content, and Home's, under the plan — so it lives here with the rest of
 * the shared chat chrome rather than in either feature.
 *
 * The trailing chevron is what makes it read as a control rather than a notice.
 * Callers render three children: the Hey Nic avatar (a bare <svg>), the comment
 * text, and an SVGWrapper chevron tagged `comment-chevron`.
 */
export const CommentCard = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 9px 10px;
  border: none;
  border-radius: 10px;
  /* Design: a very light blue wash, distinct from the white message bubbles. */
  background: ${p => p.theme.cardHoverBgColor ?? '#f5fafc'};
  color: ${bodyText};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  text-align: left;
  cursor: pointer;
  transition: 0.2s;

  /* The avatar. SVGWrapper renders a div, so this can't catch the chevron. */
  & > svg {
    flex: 0 0 auto;
    display: block;
    width: 30px;
    height: 30px;
  }

  /* Design puts 14px between the comment and the chevron, against the 10px the
     avatar gets — the gap covers 10 of it. */
  & > .comment-chevron {
    flex: 0 0 auto;
    margin-left: 4px;
    cursor: pointer;
  }

  &:hover {
    background: ${p => p.theme.primaryLighter ?? '#d4ebf2'};
  }
`

// Takes whatever width the avatar and chevron leave.
export const CommentText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`

export const DisclaimerCaption = styled.p`
  margin: 8px auto 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.4;
  color: ${bodyText};
  width: 75%;
`

export const DisclaimerLink = styled.a`
  color: ${primary};
  text-decoration: underline;
`
